import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, ShieldAlert, Plus } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { Modal, TextField, SelectField } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";

function CreateStreamModal({ open, onClose, onCreated }) {
  const [games, setGames] = useState([]);
  const [f, setF] = useState({ title: "", media_mode: "audio", audience: "public", game_id: "", affirm: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  useEffect(() => { if (open) TurfTalk.games().then((r) => setGames(r.games || [])).catch(() => {}); }, [open]);

  const submit = async () => {
    if (f.title.trim().length < 2) { toast.error("Enter a title (2+ chars)."); return; }
    if (!f.affirm) { toast.error("You must affirm the no-broadcast rule."); return; }
    setBusy(true);
    try {
      await TurfTalk.liveCreate({ title: f.title.trim(), media_mode: f.media_mode, audience: f.audience, game_id: f.game_id || null, affirm_no_broadcast: true });
      toast.success("Fan stream created");
      onCreated?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Start a fan stream" testid="create-stream-modal">
      <TextField label="Title" value={f.title} onChange={set("title")} data-testid="ls-title" maxLength={90} />
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Mode" value={f.media_mode} onChange={set("media_mode")} data-testid="ls-mode"><option value="audio">Audio</option><option value="video">Video</option></SelectField>
        <SelectField label="Audience" value={f.audience} onChange={set("audience")} data-testid="ls-audience"><option value="public">Public</option><option value="connections">Connections</option><option value="invite_only">Invite only</option><option value="private">Private</option></SelectField>
      </div>
      <SelectField label="Game (optional)" value={f.game_id} onChange={set("game_id")} data-testid="ls-game">
        <option value="">— none —</option>
        {games.map((g) => <option key={g.id} value={g.id}>{`${g.away.abbr} @ ${g.home.abbr} · ${g.league} WK${g.week}`}</option>)}
      </SelectField>
      <label className="flex items-start gap-2 text-sm text-white/70 mb-4 cursor-pointer">
        <input type="checkbox" checked={f.affirm} onChange={(e) => setF((s) => ({ ...s, affirm: e.target.checked }))} className="mt-0.5 accent-[#2F7D32]" data-testid="ls-affirm" />
        I will not stream game broadcasts, copyrighted TV/audio, or footage I don't have permission to share.
      </label>
      <button onClick={submit} disabled={busy} data-testid="ls-create-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium disabled:opacity-50">{busy ? "Creating…" : "Create stream"}</button>
    </Modal>
  );
}

export default function LivePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [meta, setMeta] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => Promise.all([
    TurfTalk.liveList({}).catch(() => ({ livestreams: [] })),
    TurfTalk.liveList({ mine: true }).catch(() => ({ livestreams: [] })),
  ]).then(([pub, mine]) => {
    const byId = {};
    for (const s of [...(mine.livestreams || []), ...(pub.livestreams || [])]) byId[s.id] = s;
    setList(Object.values(byId));
  });
  useEffect(() => { load(); TurfTalk.liveMeta().then(setMeta).catch(() => {}); }, []);

  const cancel = async (id) => { try { await TurfTalk.liveCancel(id); toast.success("Stream cancelled"); load(); } catch (e) { toast.error(e.message); } };
  const goLive = async (id) => { try { await TurfTalk.liveStart(id); navigate(`/live/${id}`); } catch (e) { toast.error(e.message); } };
  const enter = (id) => navigate(`/live/${id}`);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Fan Streams" subtitle="Fan-hosted live audio/video rooms around games and tailgates." right={
        <button onClick={() => setShowCreate(true)} data-testid="create-stream-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white text-sm font-medium"><Plus size={16} /> Start stream</button>
      } />
      {meta && meta.provider_enabled === false && (
        <div className="tt-panel rounded-xl p-4 mb-6 flex items-start gap-3">
          <ShieldAlert size={18} className="text-[#F4B740] mt-0.5" />
          <div className="text-sm text-white/70"><div className="text-white font-medium mb-0.5">Streaming provider not yet connected</div>{meta.affirmation}</div>
        </div>
      )}
      {list === null ? <div className="grid sm:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        : list.length === 0 ? <Empty title="No live streams right now" detail="Start the first fan stream (scheduling works even before the video provider is connected)." icon={Radio} />
        : (
          <div className="grid sm:grid-cols-2 gap-4">
            {list.map((s) => {
              const mine = s.host_id === user?.id || s.owner_id === user?.id;
              return (
                <div key={s.id} className="tt-panel rounded-xl p-4" data-testid={`stream-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg text-white">{s.title || "Fan stream"}</div>
                    <span className={`text-[11px] px-2 py-0.5 rounded ${s.status === "live" ? "bg-[#D92D20]/15 text-[#ff6b60]" : "bg-white/10 text-white/50"}`}>{(s.status || "scheduled").toUpperCase()}</span>
                  </div>
                  <div className="text-white/50 text-xs mt-1 flex gap-2"><span className="uppercase">{s.media_mode}</span><span className="uppercase">{s.audience}</span></div>
                  <div className="mt-3 flex gap-2">
                    {s.status === "live" && <button onClick={() => enter(s.id)} data-testid={`ls-join-${s.id}`} className="px-3 h-8 rounded-lg bg-[#D92D20] text-white text-xs font-medium">{mine ? "Enter" : "Join & watch"}</button>}
                    {mine && (s.status === "draft" || s.status === "scheduled") && <button onClick={() => goLive(s.id)} data-testid={`ls-golive-${s.id}`} className="px-3 h-8 rounded-lg bg-[#2F7D32] text-white text-xs">Go live</button>}
                    {mine && s.status !== "ended" && s.status !== "cancelled" && <button onClick={() => cancel(s.id)} data-testid={`ls-cancel-${s.id}`} className="px-3 h-8 rounded-lg tt-panel-3 text-white/80 text-xs">Cancel</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <CreateStreamModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
    </div>
  );
}
